import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowRight, UtensilsCrossed } from 'lucide-react';

const FORMATS = [
  { title: 'Breakfast Sandwiches', body: 'Pre-built, individually wrapped and ready for the morning rush.', img: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/qc47pguj_Vegan_Danish_Breakfast_Grab.png', to: '/products/breakfast-grab-and-go' },
  { title: 'Lasagnas', body: 'Gluten-free, plant-based trays for dining halls and hot bars.', img: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/dgsx38md_Vegan_Danish_Gluten_Free_Lasagna.png', to: '/products/gluten-free-lasagna' },
  { title: 'Mac & Cheese', body: 'Lunch and dinner portions — thaw, heat and serve at scale.', img: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/15vlc5gj_Vegan_Danish_Mac_Cheese_Lunch_Dinner.png', to: '/products/mac-and-cheese' },
  { title: 'Savoury Pastries', body: 'Bake-off danishes and croissants for cafés and catering.', img: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/5dqnirxr_Vegan_Danish_Pastry_Catalog.png', to: '/products/pastry-collection' },
  { title: 'Cookie Dough', body: 'Pre-portioned, ready-to-bake dough for fresh-baked programs.', img: 'https://static.prod-images.emergentagent.com/jobs/9bb1f5b4-dff8-415d-a89c-b801fc948062/images/3dea0aae98e2ae36aba32d3c570c93d0ffbd836c0a2dc213fccf633c744868b2.jpeg', to: '/wholesale#request', request: true },
  { title: 'Yogurt Parfaits', body: 'Layered grab-and-go cups for coolers and micro-markets.', img: 'https://static.prod-images.emergentagent.com/jobs/9bb1f5b4-dff8-415d-a89c-b801fc948062/images/fcec5139e89669fb6300b8f5aae455b74c8719a3ee9c699e5ea423b5be7a540b.jpeg', to: '/wholesale#request', request: true },
];

export const CampusFormats = () => {
  return (
    <section id="campus-dining" className="relative py-24 md:py-32" data-testid="campus-formats-section">
      <div className="container mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="font-script text-2xl text-leaf">beyond the pastry case</div>
            <h2 className="font-display text-4xl sm:text-5xl text-primary font-medium leading-tight mt-1">
              Campus dining &<br/>
              <span className="italic font-light text-leaf">large food formats.</span>
            </h2>
            <p className="mt-5 text-muted-foreground text-base md:text-lg">
              From individually wrapped breakfast sandwiches to full trays for the hot line —
              one plant-based partner covering every daypart and every format.
            </p>
          </div>
          <Link to="/products" className="inline-flex items-center gap-2 text-primary font-medium hover:text-leaf transition-colors" data-testid="campus-formats-browse-all">
            Browse all foodservice products <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FORMATS.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.06 }}
            >
              <Link
                to={f.to}
                className="group relative flex flex-col h-full rounded-3xl bg-card border border-border/70 overflow-hidden shadow-soft hover:shadow-pop hover:-translate-y-1.5 transition-all duration-500"
                data-testid={`campus-format-${f.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
                  <img src={f.img} alt={f.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1.6s]" />
                  {f.request && (
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-background/85 backdrop-blur text-[10px] uppercase tracking-[0.16em] text-primary font-medium">
                      Available by request
                    </div>
                  )}
                  <div className="absolute top-3 right-3 h-9 w-9 rounded-full bg-background/85 backdrop-blur grid place-items-center text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-xl font-semibold text-primary leading-tight">{f.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.body}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          <UtensilsCrossed className="h-3.5 w-3.5 text-leaf" /> Plus bagels, comfort snacks & more foodservice formats on request
        </div>
      </div>
    </section>
  );
};

export default CampusFormats;
